import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
    CHATGPT_APP_URL, CLAUDE_CONNECTOR_URL, CONNECTOR_NAME, MCP_URL, MUSE_PASTE_BLOCK, MUSE_URL
} from './connect-apps-dock.component';

type App = 'claude' | 'muse';

/**
 * The first screen of the landing page: connect LIAM to ChatGPT, Claude or
 * Muse, with the setup for each right there.
 *
 * Most visitors arrive on a phone wanting to know how to switch LIAM on in the
 * app they already use. The story still follows, below the fold; it is no
 * longer the thing standing between them and the setup.
 *
 * Same values and flows as the "Your apps" dock (which this page hides, since
 * it would only repeat these cards), laid out for a phone: each card opens in
 * place instead of as a popover above a bar.
 */
@Component({
    selector: 'app-connect-hero',
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: './connect-hero.component.html',
    styleUrls: ['./connect-hero.component.css']
})
export class ConnectHeroComponent {
    readonly chatgptAppUrl = CHATGPT_APP_URL;
    readonly claudeConnectorUrl = CLAUDE_CONNECTOR_URL;
    readonly connectorName = CONNECTOR_NAME;
    readonly mcpUrl = MCP_URL;
    readonly museUrl = MUSE_URL;
    readonly musePasteBlock = MUSE_PASTE_BLOCK;

    open: App | null = null;
    copiedField: 'name' | 'url' | 'muse' | null = null;
    /** False when the browser refused the clipboard, so the card says "copy it yourself". */
    museCopyOk = true;

    toggle(app: App): void {
        const opening = this.open !== app;
        this.open = opening ? app : null;
        this.copiedField = null;
        // The click that opens Muse is the user gesture the clipboard needs,
        // so the message is copied now and the card shows what was copied.
        if (opening && app === 'muse') {
            this.copyMuse();
        }
    }

    copyField(field: 'name' | 'url'): void {
        const value = field === 'name' ? this.connectorName : this.mcpUrl;
        navigator.clipboard?.writeText(value).catch(() => { });
        this.flash(field);
    }

    copyMuse(): void {
        if (!navigator.clipboard) {
            this.museCopyOk = false;
            return;
        }
        navigator.clipboard.writeText(this.musePasteBlock).then(
            () => {
                this.museCopyOk = true;
                this.flash('muse');
            },
            () => (this.museCopyOk = false)
        );
    }

    scrollToStory(): void {
        document.getElementById('why')?.scrollIntoView({ behavior: 'smooth' });
    }

    private flash(field: 'name' | 'url' | 'muse'): void {
        this.copiedField = field;
        setTimeout(() => {
            if (this.copiedField === field) {
                this.copiedField = null;
            }
        }, 2000);
    }
}
